/**
 * The per-conversation menu in the rail: rename, pin, move to a group, delete.
 *
 * Demo page: NextAdmin `(dashboard)/(home)/_component/top-products` (ghost icon button + `ActionMenu`) and
 * /ui-elements/dialogs for the rename, group and delete dialogs.
 *
 * Every item is a write, so the whole menu sits inside `<Can>` for the policies the conversation routes require
 * (`PATCH` and `DELETE /conversations/:id` and `/conversations/groups`: `requireAnyPolicy('CanManageAgents',
 * 'CanConverse')`, server/src/api/routes/conversations.ts and conversation-groups.ts). The trigger is only shown on
 * row hover, while the menu is open (`aria-expanded`), or on keyboard focus.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FolderInput, Pencil, Pin, PinOff, Trash2 } from 'lucide-react';
import { useState } from 'react';
import {
  deleteConversationMutation,
  listConversationGroupsQueryKey,
  listConversationsQueryKey,
  updateConversationMutation,
} from '../../generated/api/@tanstack/react-query.gen.js';
import { Can } from '../../components/auth/Can.js';
import { ActionMenu } from '../../components/ui/ActionMenu.js';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog.js';
import { useToast } from '../../components/ui/Toast.js';
import { ConversationGroupDialog } from './ConversationGroupDialog.js';
import { ConversationRenameDialog } from './ConversationRenameDialog.js';

interface Props {
  conversationId: string;
  topic: string;
  pinned: boolean;
}

type Dialog = 'rename' | 'group' | 'delete' | null;

const CONVERSATION_WRITE_POLICIES = ['CanManageAgents', 'CanConverse'];

export function ConversationRowMenu({ conversationId, topic, pinned }: Props) {
  const queryClient = useQueryClient();
  const { notify } = useToast();
  const [dialog, setDialog] = useState<Dialog>(null);
  const close = () => setDialog(null);

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: listConversationsQueryKey() });
    void queryClient.invalidateQueries({ queryKey: listConversationGroupsQueryKey() });
  };

  const pin = useMutation({
    ...updateConversationMutation(),
    onSuccess: refresh,
    onError: (error) => notify(error.message, 'danger'),
  });
  const remove = useMutation({
    ...deleteConversationMutation(),
    onSuccess: () => {
      refresh();
      close();
    },
    onError: (error) => notify(error.message, 'danger'),
  });

  return (
    <Can policies={CONVERSATION_WRITE_POLICIES} mode="any">
      <ActionMenu
        aria-label={`Options for ${topic}`}
        className="opacity-0 group-hover:opacity-100 focus-visible:opacity-100 aria-expanded:opacity-100"
        items={[
          { id: 'rename', label: 'Rename', icon: <Pencil />, onAction: () => setDialog('rename') },
          {
            id: 'pin',
            label: pinned ? 'Unpin' : 'Pin',
            icon: pinned ? <PinOff /> : <Pin />,
            isDisabled: pin.isPending,
            onAction: () => pin.mutate({ path: { id: conversationId }, body: { pinned: !pinned } }),
          },
          { id: 'group', label: 'Move to group', icon: <FolderInput />, onAction: () => setDialog('group') },
          { id: 'delete', label: 'Delete', icon: <Trash2 />, tone: 'danger', onAction: () => setDialog('delete') },
        ]}
      />

      {dialog === 'rename' && (
        <ConversationRenameDialog conversationId={conversationId} topic={topic} onClose={close} onRenamed={refresh} />
      )}
      {dialog === 'group' && (
        <ConversationGroupDialog conversationId={conversationId} onClose={close} onMoved={refresh} />
      )}
      <ConfirmDialog
        open={dialog === 'delete'}
        onClose={close}
        title="Delete conversation"
        message={`Delete "${topic}"? Only the owner of a conversation can delete it.`}
        confirmLabel="Delete conversation"
        loading={remove.isPending}
        onConfirm={() => remove.mutate({ path: { id: conversationId } })}
      />
    </Can>
  );
}
